import React, { useEffect, useRef } from 'react';
import * as echarts from 'echarts';
import { Typography } from 'antd';

const { Text } = Typography;

/**
 * BH/FH时间序列柱状图组件
 * 每个飞机一行，横向展示时间趋势
 */
const BhFhTimeSeriesChart = ({ 
    aircraftReg, 
    data, 
    height = 140,
    showLabels = true 
}) => {
    const chartRef = useRef(null);
    const chartInstance = useRef(null);

    useEffect(() => {
        if (!chartRef.current || !data) return;

        // 初始化图表实例
        if (!chartInstance.current) {
            chartInstance.current = echarts.init(chartRef.current);
        }

        // 格式化日期显示
        const formatDate = (dateStr) => {
            const date = new Date(dateStr);
            return `${date.getMonth() + 1}/${date.getDate()}`;
        };

        const dates = data.dates.map(formatDate);
        
        const option = {
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'shadow' },
                formatter: (params) => {
                    const index = params[0].dataIndex;
                    const date = data.dates[index];
                    const legCount = data.leg_counts?.[index];
                    const legInfo = legCount != null ? `<br/>航段数: ${legCount}` : '';
                    return `<strong>${aircraftReg}</strong> - ${date}<br/>
                            ${params[0].marker}平均 FH: ${params[0].value}h/航段<br/>
                            ${params[1].marker}平均 BH: ${params[1].value}h/航段${legInfo}`;
                },
                textStyle: { fontSize: 11 }
            },
            grid: {
                left: '3%',
                right: '3%',
                bottom: '15%',
                top: showLabels ? '20%' : '10%',
                containLabel: true
            },
            xAxis: {
                type: 'category',
                data: dates,
                axisLabel: {
                    rotate: 45,
                    fontSize: 9,
                    color: '#8c8c8c',
                    interval: dates.length <= 40 ? 0 : Math.floor(dates.length / 10)
                },
                axisLine: { lineStyle: { color: '#d9d9d9' } },
                axisTick: { show: false }
            },
            yAxis: {
                type: 'value',
                axisLabel: {
                    fontSize: 9,
                    color: '#8c8c8c',
                    formatter: '{value}'
                },
                splitLine: { lineStyle: { color: '#f0f0f0' } },
                axisLine: { show: false }
            },
            series: [
                {
                    name: 'FH',
                    type: 'bar',
                    data: data.fh_values,
                    itemStyle: { color: '#fa8c16' },
                    label: {
                        show: showLabels,
                        position: 'top',
                        formatter: (params) => params.value != null ? Number(params.value).toFixed(1) : '',
                        fontSize: 10,
                        color: '#fa8c16',
                        fontWeight: 500
                    },
                    barGap: '15%',
                    barWidth: '30%'
                },
                {
                    name: 'BH',
                    type: 'bar',
                    data: data.bh_values,
                    itemStyle: { color: '#1890ff' },
                    label: {
                        show: showLabels,
                        position: 'top',
                        formatter: (params) => params.value != null ? Number(params.value).toFixed(1) : '',
                        fontSize: 10,
                        color: '#1890ff',
                        fontWeight: 500
                    },
                    barWidth: '30%'
                }
            ]
        };

        chartInstance.current.setOption(option);

        // 响应式调整
        const handleResize = () => {
            chartInstance.current?.resize();
        };
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [aircraftReg, data, showLabels]);

    // 组件卸载时销毁图表
    useEffect(() => {
        return () => {
            chartInstance.current?.dispose();
            chartInstance.current = null;
        };
    }, []);

    if (!data || data.dates.length === 0) {
        return (
            <div style={{ 
                height, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                color: '#8c8c8c',
                fontSize: '12px'
            }}>
                暂无数据
            </div>
        );
    }

    return (
        <div 
            ref={chartRef} 
            style={{ 
                width: '100%', 
                height: `${height}px`,
                background: 'white',
                borderRadius: '4px'
            }} 
        />
    );
};

/**
 * BH/FH图表行容器组件
 * 左侧显示飞机号，右侧显示图表
 */
export const BhFhChartRow = ({ 
    aircraftReg, 
    data, 
    loading = false 
}) => {
    return (
        <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '12px',
            padding: '8px',
            background: '#fafafa',
            borderRadius: '4px',
            border: '1px solid #f0f0f0',
            transition: 'all 0.3s'
        }}
        onMouseEnter={(e) => {
            e.currentTarget.style.background = '#f5f5f5';
            e.currentTarget.style.borderColor = '#d9d9d9';
        }}
        onMouseLeave={(e) => {
            e.currentTarget.style.background = '#fafafa';
            e.currentTarget.style.borderColor = '#f0f0f0';
        }}>
            {/* 飞机号标签 */}
            <div style={{
                minWidth: '100px',
                maxWidth: '100px',
                fontSize: '13px',
                fontWeight: 600,
                color: '#1890ff',
                textAlign: 'center',
                padding: '8px 12px',
                background: 'white',
                borderRadius: '4px',
                border: '1px solid #e6f7ff',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
            }}>
                {aircraftReg}
            </div>

            {/* 图表容器 */}
            <div style={{ flex: 1 }}>
                {loading ? (
                    <div style={{ 
                        height: '140px', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        color: '#8c8c8c',
                        fontSize: '12px'
                    }}>
                        加载中...
                    </div>
                ) : (
                    <BhFhTimeSeriesChart 
                        aircraftReg={aircraftReg} 
                        data={data} 
                        height={140}
                        showLabels={data?.dates?.length <= 30}
                    />
                )}
            </div>
        </div>
    );
};

export default BhFhTimeSeriesChart;
